/**
 * Смуга лічильників (аудит № 21; stability.md, розд. 4): усі пакети лічильників вкладки —
 * `POST /api/v1/views/counts` (`view-count.ts`) і `POST …/board/counts` (`boardCountsQuery`,
 * `board/board-data.ts`) — ідуть через одну чергу, обидва читанням `api(…, { read: true })`.
 *
 * Сервер пускає один пакет лічильників на людину одночасно (`COUNTS_PER_PRINCIPAL`,
 * packages/platform/src/views-http.ts), спільно для подань і дошки; другий одночасний — `429`.
 * Клієнт раніше переривав запит у дорозі (перемонтування, `StrictMode`) і одразу слав новий —
 * сервер ще рахував перший, і новий отримував `429`, а лічильник зникав. Тепер:
 * - **не більше `COUNTS_CONCURRENCY` пакетів одночасно**, решта чекає в черзі;
 * - **відправлене не переривається**: скасування глядача лише відв'язує його від відповіді;
 * - **той самий пакет (ключ) не шлеться вдруге**, поки перший у черзі чи в дорозі: новий глядач
 *   приєднується до нього (повторне монтування після скасування);
 * - **`429` — тихий повтор один раз** через `Retry-After` (≤ 10 с), не залишаючи черги; далі
 *   помилка йде викликачу, і той показує «—» чи попереднє значення, а не стан помилки екрана.
 *
 * Запит, який скасували всі глядачі ще до відправлення, з черги знімається без мережі.
 */
import { READ_QUIET_WAIT_S, retryAfterOf } from './api.ts';

/** Одночасних пакетів від вкладки — як межа сервера на людину (`COUNTS_PER_PRINCIPAL`). */
export const COUNTS_CONCURRENCY = 1;

interface Waiter<T> {
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

interface Job<T> {
  key: string;
  send: (signal: AbortSignal) => Promise<T>;
  waiters: Set<Waiter<T>>;
  started: boolean;
}

const abortError = (signal: AbortSignal): unknown =>
  signal.reason ?? new DOMException('Aborted', 'AbortError');

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class CountsLane {
  private readonly queue: Job<unknown>[] = [];
  private readonly jobs = new Map<string, Job<unknown>>();
  private running = 0;
  /** Покоління смуги: скидання для тестів забуває пакети в дорозі. */
  private generation = 0;

  constructor(
    private readonly concurrency = COUNTS_CONCURRENCY,
    private readonly wait: (ms: number) => Promise<unknown> = sleep,
  ) {}

  /**
   * Пакет із ключем `key` (однакове тіло — однаковий ключ). `signal` глядача відв'язує його від
   * відповіді; запит у дорозі він не перериває.
   */
  run<T>(key: string, send: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> {
    if (signal?.aborted) return Promise.reject(abortError(signal));
    return new Promise<T>((resolve, reject) => {
      let job = this.jobs.get(key) as Job<T> | undefined;
      if (!job) {
        job = { key, send, waiters: new Set(), started: false };
        this.jobs.set(key, job as Job<unknown>);
        this.queue.push(job as Job<unknown>);
      }
      const waiter: Waiter<T> = { resolve, reject };
      job.waiters.add(waiter);
      const current = job;
      signal?.addEventListener(
        'abort',
        () => {
          if (!current.waiters.delete(waiter)) return;
          reject(abortError(signal));
          if (!current.started && current.waiters.size === 0) this.drop(current as Job<unknown>);
        },
        { once: true },
      );
      this.pump();
    });
  }

  /** Пакетів у дорозі й у черзі (для тестів). */
  get size(): { running: number; queued: number } {
    return { running: this.running, queued: this.queue.length };
  }

  private drop(job: Job<unknown>) {
    const at = this.queue.indexOf(job);
    if (at >= 0) this.queue.splice(at, 1);
    this.jobs.delete(job.key);
  }

  /** Забути чергу й пакети в дорозі (стан модуля між тестами, test/setup.ts). */
  resetForTests(): void {
    this.generation++;
    this.queue.length = 0;
    this.jobs.clear();
    this.running = 0;
  }

  private pump() {
    while (this.running < this.concurrency && this.queue.length > 0) {
      const job = this.queue.shift() as Job<unknown>;
      job.started = true;
      this.running++;
      const generation = this.generation;
      void this.execute(job).finally(() => {
        if (generation !== this.generation) return;
        this.running--;
        this.pump();
      });
    }
  }

  private async execute(job: Job<unknown>) {
    // Сигнал запиту ніхто не скасовує: відправлене доходить до кінця (див. вище).
    const signal = new AbortController().signal;
    try {
      let value: unknown;
      try {
        value = await job.send(signal);
      } catch (error) {
        const wait = retryAfterOf(error);
        if (wait === null || wait > READ_QUIET_WAIT_S) throw error;
        await this.wait(wait * 1000);
        value = await job.send(signal);
      }
      this.settle(job);
      for (const w of job.waiters) w.resolve(value);
    } catch (error) {
      this.settle(job);
      for (const w of job.waiters) w.reject(error);
    }
  }

  /** Відповідь є: новий глядач з тим самим ключем уже піде новим пакетом, а не до цього. */
  private settle(job: Job<unknown>) {
    if (this.jobs.get(job.key) === job) this.jobs.delete(job.key);
  }
}

/** Смуга вкладки: одна на подання й дошку, бо межа сервера в них спільна. */
export const countsLane = new CountsLane();
